import React from 'react';
import { 
  TrendingDown, 
  Shield, 
  Database, 
  CheckCircle, 
  Zap, 
  Users,
  Eye
} from 'lucide-react';

const WhyKeops: React.FC = () => {
  const solutions = [
    {
      icon: TrendingDown,
      title: "COST OPTIMIZATION",
      description: "Eliminate overheads (salaries, training, tech infrastructure) via scalable pay-per-report models, especially for niche requirements like PAI or TPT",
      benefit: "Turn fixed costs into variable, predictable expenses",
      color: "green"
    },
    {
      icon: Shield,
      title: "REGULATORY AGILITY",
      description: "Automatic updates for evolving templates (e.g. AIFMD, TPT, PRIIPs, SFDR) handled by providers' dedicated regulatory watch teams",
      benefit: "Eliminate non-compliance risk",
      color: "blue"
    },
    {
      icon: Database,
      title: "DATA HARMONIZATION",
      description: "Centralized ingestion of fragmented data (fund accounting, ESG vendors, loan tapes) into unified, validated formats",
      benefit: "Slash reconciliation time by 80%",
      color: "purple"
    },
    {
      icon: CheckCircle,
      title: "AUTOMATED QUALITY CONTROL",
      description: "Embedded validation rules (e.g., CSSF business logic checks, KID cost formula audits) flag errors pre-submission",
      benefit: "Avoid costly regulatory rejections",
      color: "indigo"
    },
    {
      icon: Zap,
      title: "CALCULATION & DISSEMINATION ENGINE",
      description: "Streamlined workflows for complex metrics (PAI, PRIIPs scenarios, KPP performance) with auto-generation/distribution of reports",
      benefit: "Accelerate reporting cycles",
      color: "orange"
    },
    {
      icon: Users,
      title: "SCALABILITY & EXPERTISE",
      description: "On-demand access to specialists (e.g., ESG analysts, loan data mappers) and elastic capacity for peak periods",
      benefit: "Scale resources without hiring overhead",
      color: "teal"
    },
    {
      icon: Eye,
      title: "REGULATORY CHANGES WATCHDOG",
      description: "Keops keeps monitoring articles, updates, changes and guidelines from regulators to stay current with requirements",
      benefit: "Never be non-compliant again",
      color: "red"
    }
  ];

  return (
    <section id="why-keops" className="bg-white py-12 sm:py-16 lg:py-20 bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black mb-4">
            Why Keops?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto text-gray-800">
             We turn the fund risk compliance challenges into competitive advantages through automation, expertise, and scalable solutions
          </p>
        </div>

        {/* Solution Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {solutions.map((solution, index) => {
            const IconComponent = solution.icon;
            return (
              <div 
                key={index}
                className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 p-6 lg:p-8"
              >
                {/* Icon */}
                <div className="flex items-center justify-center w-12 h-12 lg:w-14 lg:h-14 bg-blue-900 rounded-lg mb-4">
                  <IconComponent className="w-6 h-6 lg:w-7 lg:h-7 text-blue-200" />
                </div>

                {/* Title */}
                <h3 className="text-lg lg:text-xl font-semibold text-black mb-3">
                  {solution.title}
                </h3>

                {/* Description */}
                <p className="text-sm lg:text-base text-black mb-4 leading-relaxed">
                  {solution.description}
                </p>

                {/* Benefit */}
                <div className="border-t border-grey pt-4">
                  <p className="text-xs lg:text-sm font-medium text-blue-500 uppercase tracking-wide mb-1">
                    Key Benefit
                  </p>
                  <p className="text-sm lg:text-base text-gray-800 font-medium">
                    {solution.benefit}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyKeops;
